import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { IconDownload } from './Icons';
import { mapRowToTender } from '../lib/tenders';
import WorkflowIndicator from './WorkflowIndicator';
import './Tenders.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const tenderDetailsSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

function getStatusBadgeClass(status) {
  const s = (status || '').toLowerCase().replace(/\s+/g, '-');
  if (s === 'draft') return 'tenders-badge-draft';
  if (s === 'submitted') return 'tenders-badge-submitted';
  if (s === 'under-review') return 'tenders-badge-under-review';
  if (s === 'awarded') return 'tenders-badge-awarded';
  if (s === 'lost') return 'tenders-badge-lost';
  if (s === 'cancelled') return 'tenders-badge-cancelled';
  return 'tenders-badge-draft';
}

function getApprovedBadgeClass(approved) {
  const a = (approved || '').toLowerCase();
  if (a.includes('pending')) return 'tenders-badge-pending';
  if (a === 'approved') return 'tenders-badge-approved';
  if (a === 'rejected') return 'tenders-badge-rejected';
  return 'tenders-badge-not-approved';
}

/** Derive workflow stage from tender for the 6-step indicator. */
function tenderToWorkflowStage(tender) {
  if (!tender) return 'Tender Created';
  const s = (tender.status || '').toLowerCase();
  const approved = tender.approved === 'Approved';
  if (s === 'awarded') return 'Awarded';
  if (s === 'lost' || s === 'cancelled') return 'Evaluation';
  if (s === 'under review') return 'Evaluation';
  if (s === 'submitted') return 'Submitted';
  if (approved && s !== 'draft') return 'Proposal Prepared';
  if (approved) return 'Approved';
  return tender.status === 'Draft' ? 'Tender Created' : 'Approved';
}

export default function TenderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [tender, setTender] = useState(null);
  const [tenderDocs, setTenderDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      tenderDetailsSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { error: sessionErr } = await tenderDetailsSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setTender(null);
        setTenderDocs([]);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([
        tenderDetailsSupabase.from('tenders').select('*').eq('id', id).maybeSingle(),
        timeoutPromise,
      ]);
      if (cancelled) return;
      const { data, error: err } = res;
      if (err) {
        setError(err.message);
        setTender(null);
        setTenderDocs([]);
      } else {
        setTender(data ? mapRowToTender(data) : null);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, runWakeUp]);

  useEffect(() => {
    if (!tender?.id) {
      setTenderDocs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await tenderDetailsSupabase.from('tender_documents').select('*').eq('tender_id', tender.id).order('created_at', { ascending: true });
      if (!cancelled) setTenderDocs(data ?? []);
    })();
    return () => { cancelled = true; };
  }, [tender?.id]);

  const handleDownloadDoc = useCallback(async (storagePath) => {
    await runWakeUp();
    const { data } = await tenderDetailsSupabase.storage.from('tender-documents').createSignedUrl(storagePath, 60);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  }, [runWakeUp]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !id) return;
      (async () => {
        await tenderDetailsSupabase.auth.getSession();
        await runWakeUp();
        const { data, error: err } = await tenderDetailsSupabase.from('tenders').select('*').eq('id', id).maybeSingle();
        if (!err && data) setTender(mapRowToTender(data));
        const { data: docs } = await tenderDetailsSupabase.from('tender_documents').select('*').eq('tender_id', id).order('created_at', { ascending: true });
        if (docs) setTenderDocs(docs);
      })();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [id, runWakeUp]);
  const workflowStage = tenderToWorkflowStage(tender);
  const hasDocs = tenderDocs.length > 0;
  const isApprovedForConvert = tender?.approved === 'Approved';
  const canConvertToProposal = isApprovedForConvert && hasDocs;

  if (loading) {
    return (
      <div className="tenders-details-page">
        <div className="tenders-details-card">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !tender) {
    return (
      <div className="tenders-details-page">
        <div className="tenders-details-card">
          <p role="alert">{error || 'Tender not found.'}</p>
          <button type="button" className="tenders-footer-btn" onClick={() => navigate('/tenders')}>
            Back to Tender List
          </button>
        </div>
      </div>
    );
  }

  const isApproved = tender.approved === 'Approved';
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'documents', label: 'Documents' },
  ];

  return (
    <div className="tenders-details-page">
      <div className="tenders-details-card">
        <button type="button" className="tenders-details-back" onClick={() => navigate('/tenders')}>
          ← Back to Tender List
        </button>
        <h1 className="tenders-title" style={{ marginBottom: '1rem' }}>{tender.title}</h1>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>{tender.tenderId}</p>

        <WorkflowIndicator currentStage={workflowStage} />

        <div className="tenders-details-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`tenders-details-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            <div className="tenders-info-card">
              <div className="tenders-info-grid">
                <div className="tenders-info-item">
                  <label>Client</label>
                  <span>{tender.client}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Department</label>
                  <span>{tender.department}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Submission deadline</label>
                  <span>{tender.submissionDeadline || '—'}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Status</label>
                  <span className={`tenders-badge ${getStatusBadgeClass(tender.status)}`}>{tender.status}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Approved</label>
                  <span className={`tenders-badge ${getApprovedBadgeClass(tender.approved)}`}>{tender.approved}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Owner / Assigned to</label>
                  <span>{tender.assignedTo || '—'}</span>
                </div>
              </div>
            </div>
            {tender.notes && (
              <section style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}>Notes</label>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{tender.notes}</p>
              </section>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1.25rem' }}>
              <button type="button" className="tenders-header-btn primary">
                Approve Tender
              </button>
              <button
                type="button"
                className={`tenders-header-btn primary ${!canConvertToProposal ? 'tenders-btn-disabled' : ''}`}
                disabled={!canConvertToProposal}
                title={!isApprovedForConvert ? 'Tender must be approved first' : !hasDocs ? 'Upload at least one document first' : 'Create proposal from this tender'}
                onClick={() => canConvertToProposal && navigate('/tenders/proposals', { state: { convertFromTenderId: tender.id } })}
              >
                Convert to Proposal
              </button>
              <button type="button" className="tenders-header-btn" onClick={() => setActiveTab('documents')}>
                Upload Documents
              </button>
              <button
                type="button"
                className={`tenders-header-btn ${!isApproved ? 'tenders-btn-disabled' : ''}`}
                disabled={!isApproved}
                title={!isApproved ? 'Only when Approved = Approved' : ''}
              >
                Convert to Estimate
              </button>
            </div>
          </>
        )}

        {activeTab === 'documents' && (
          <>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>Upload and manage documents from the Edit Tender form. Use View / Download to open or save a file.</p>
            <ul className="tenders-doc-list">
              {tenderDocs.length === 0 ? (
                <li style={{ padding: '1rem', color: 'var(--text-muted)' }}>No documents uploaded yet.</li>
              ) : (
                tenderDocs.map((doc) => (
                  <li key={doc.id} className="tenders-doc-item">
                    <span>{doc.display_name || doc.file_name}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{doc.document_type} · v{doc.version} · {doc.created_at?.slice(0, 10)} · {doc.uploaded_by || '—'}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="View or download"
                        title="View / Download (opens in new tab)"
                        onClick={() => handleDownloadDoc(doc.storage_path)}
                      >
                        <IconDownload />
                      </button>
                      <button
                        type="button"
                        className="tenders-footer-btn"
                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                        onClick={() => handleDownloadDoc(doc.storage_path)}
                      >
                        View / Download
                      </button>
                    </span>
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
